import { dataCache } from '../../services/CacheService';
import { getInObject } from './apiDataUtils';
import Status from './status';
import { FormatType, formatValue, RateType } from '../../utilities/formatValue';
import HttpService from '../../services/HttpService';

const ResourceToApiMap = {
    'TabbedRates': 'rates/tabbed',
    'BusinessRates': 'rates/business',
};

const HttpGatewayTimeoutCode = 504;
const HttpRequestTimeoutCode = 408;
const AxiosTimeoutCode = 'ECONNABORTED';

/**
 * Request Api data and cache returned promise to prevent double requests,
 * data is cached in memory and flushed on a page refresh
 * @param endpoint Base Api URL
 * @param resource Ex: TabbedRates
 * @param id API parameter
 * @returns Promise<Object>
 */
async function requestData(resource: string, id: string) {
    const key = `${resource}:${id}`;
    if (!resource) {
        console.error(`DataSource/requestData: invalid resource`);
        return null;
    }
    const request = `${process.env.GATSBY_API_URL}/${resource}${id !== '*' ? `/${id}` : ''}`;
    return HttpService.get(request, key, {
        timeout: Number(process.env.GATSBY_WEBSITE_API_TIMEOUT_MS || 8000),
        headers: {
            'Content-Type': 'application/json'
        }
    }).catch((error) => {
        console.error(`DataSource/requestData: error on request "${request}"`, error);
        const status = error?.response?.status;
        const code = error.code;
        if (code === AxiosTimeoutCode || status === HttpGatewayTimeoutCode || status === HttpRequestTimeoutCode) {
            return Status.TIMEOUT;
        }
        return Status.ERROR;
    });
}

/**
 * Get all data based on apiData or tableData in page props from cache variable,
 * it's sync so it prevents double rendering for less react re-renders
 * @param dataPageProps string[] with list of resource from apiData or tableData in page props
 * @returns Object | null
 */
export function getDataFromCache(dataPageProps: string[]) {
    const apiData = {};
    for (const dataEntry of dataPageProps) {
        if (dataCache[dataEntry] !== undefined) {
            const resourceValue = dataCache[dataEntry];
            const [resource, id] = dataEntry.split(':');
            apiData[resource] = apiData[resource] || {};
            if (id === '*') {
                Object.assign(apiData[resource], resourceValue);
            } else {
                apiData[resource][id] = resourceValue;
            }
        } else {
            return undefined;
        }
    }
    return apiData;
}

/**
 * Takes the raw data from several api endpoints and merge it into a single
 * object for quick lookup
 * @param data: array with responses from api endpoints
 * @returns Object
 */
function formatApiData(data: any[]) {
    return data.reduce((acc, {resource, id, data}) => {
        acc[resource] = acc[resource] || {};
        acc[resource][id] = data;
        return acc;
    }, {});
}

/**
 * Request and cache data from Data API, this is async so should only be called if
 * getDataFromCache could not retrieve data since this will force a re-render in react
 * @param endpoint Base Api URL
 * @param apiDataPageProps string[] with list of resource from apiData in page props
 * @returns Promise<Object>
 */
export async function getApiData(apiDataPageProps: string[]) {
    const apiRequests = apiDataPageProps.map(async dataEntry => {
        const [resource, id] = dataEntry.split(':');
        const path = ResourceToApiMap[resource];
        const data = await requestData(path, id);
        if (id === '*' && Array.isArray(data)) {
            dataCache[dataEntry] = data.reduce((obj, dataItem) => ({
                [dataItem.id]: dataItem,
                ...obj
            }), {});
        } else {
            dataCache[dataEntry] = data;
        }
        return { resource, id, data };
    });
    return Promise.all(apiRequests).then(data => formatApiData(data));
}


/**
 * Request and cache data from Custom Table API, as getApiData this is async
 * @param endpoint Base Api URL
 * @param apiDataPageProps string[] with list of resource from apiData in page props
 * @returns Promise<Object>
 */
 export async function getTableData(tableDataPageProps: string[]) {
    const apiRequests = tableDataPageProps.map(async dataEntry => {
        const [resource, id] = dataEntry.split(':');
        const data = await requestData(`table/${resource}`, id);
        if (id === '*' && Array.isArray(data)) {
            dataCache[dataEntry] = data.reduce((obj, dataItem) => ({
                [dataItem.CodeName]: dataItem,
                ...obj
            }), {});
        } else {
            dataCache[dataEntry] = data;
        }
        return { resource, id, data };
    });
    return Promise.all(apiRequests).then(data => formatApiData(data));
}

/**
 * Retrieve single field from API Source Data and validates if requested data is correct
 * @param apiData Api Data
 * @param dataPath Path to data item in Api Data, ex:  TabbedRates.9.id
 * @param status is the data is still loading or final, don't log errors until all data is loaded
 * @param precision number of decimal digits to show if data is a number or null to show as it is (defaults to 3)
 * @param formatType (optional) how to format the data if numeric (%, $, #)
 */
export function getApiDataByPath(apiData, dataPath: string, status: Symbol, precision: number | null, formatType?: FormatType) {
    try {
        let pathSections = dataPath.split('.');
        if (pathSections.length === 1) pathSections = ['variables', dataPath];

        let [resource, id] = pathSections;

        if (!id) throw new Error('Invalid ID');
        if (!apiData[resource]) throw new Error(`No ${resource} data for ${dataPath}`);
        if (apiData[resource][id] === undefined) {
            throw new Error(`Make sure ${resource}:${id} is included in PageProps / Data Sources`);
        }
        if (apiData[resource][id] === Status.TIMEOUT) {
            throw new Error(`Request for ${resource}:${id} timed out`);
        }
        if (apiData[resource][id] === Status.ERROR) {
            throw new Error(`Request for ${resource}:${id} failed`);
        }
        const valueStr = getInObject(apiData, pathSections);
        if (valueStr === undefined) throw new Error(`Variable ${dataPath} does not exist`);
        if (valueStr === null) return '';
        if (typeof valueStr === 'object') return JSON.stringify(valueStr);

        // if the data value is a rate, build a data test object and send to the formatter to use for test automation (adds data-test-id and other data-test attributes to markup)
        let dataTestRateObj;
        if (pathSections.includes('rate') &&
            pathSections.find((p: any) => Object.values(RateType).includes(p)) &&
            (resource === 'TabbedRates' || resource === 'BusinessRates')) {
            dataTestRateObj = {
                id,
                resource,
                rateType: pathSections.find((p: any) => Object.values(RateType).includes(p)),
                precision,
            };
        }

        return formatValue({
            dataTestRateObj,
            valueStr,
            precision,
            formatType,
        });
    } catch(err) {
        if (status !== Status.LOADING) {
            console.error(`Data source validation: ${err.message}`);
            return `[N/A]`;
        } else{
            return '';
        }
    }
}
